import { Flex, Spinner } from '@chakra-ui/react';
import {
  forwardRef,
  useImperativeHandle,
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
} from 'react';
import { KText } from '@/ui-library/components';
import { useOfferStore } from '../../store';
import { fetchSelectOptions } from '@/components/formBuilder/helpers/formHelpers';
import { useGetTemplatesVariablesQuery } from '@/store/query/email-templates-settings';
import { getTemplatesCategoryConfig } from '@/constants/templates-category';
import { OfferEmailForm, type OfferEmailFormRef } from './OfferEmailForm';
import '../offer.scss';
import { EsignEmailForm, type EsignEmailFormRef } from './EsignEmailForm';
import { offerDetailsAPI } from '../../api';
import { buildOfferEmailAttachments } from '../../helpers';

export interface OfferEmailRef {
  getModel: () => Promise<{
    hasValidData: boolean;
    payload: any;
  }>;
}

export interface OfferEmailProps {
  type?: 'create' | 'edit';
  selectSignatureType?: 'kula' | 'docusign' | 'pandadoc' | 'adobe';
}

const CATEGORY = ['candidate_offer_notice'];
const CHANNEL = 'email';

export const OfferEmail = forwardRef((props: any, comRef: any) => {
  const { selectSignatureType = 'kula' } = props;

  const offerId = useOfferStore((state) => state.offerId);
  const applicationData = useOfferStore((state) => state.applicationData);
  const currentOffer = useOfferStore((state) => state.currentOffer);
  const offerAttachments = useOfferStore((state) => state.offerAttachments);
  const formRef = useRef<OfferEmailFormRef>(null);
  const esignFormRef = useRef<EsignEmailFormRef>(null);

  const [templates, setTemplates] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState<any>({});

  const templatesCategoryConfig = getTemplatesCategoryConfig();
  const view = [...templatesCategoryConfig.candidate_adhoc.view, 'offer'];

  const { data: { variables = [] } = {} } = useGetTemplatesVariablesQuery({
    params: { view },
  });

  const contactId = applicationData?.contact?.id;

  // Read additional files from the offerAttachments store rather than refetching the offer
  // (CORE-4800); a second staleTime:0 observer here drove an infinite refetch loop.
  const attachments = useMemo(
    () =>
      buildOfferEmailAttachments(
        currentOffer,
        offerAttachments ?? currentOffer?.attachments ?? []
      ),
    [currentOffer, offerAttachments]
  );

  const buildEndPoint = useMemo(() => {
    const categories = CATEGORY.map(
      (cat) => `&filter_by[category][]=${cat}`
    ).join('');
    return `/ats_comm_templates?filter_by[channel]=${CHANNEL}${categories}&items=200&filter_by[name]=`;
  }, []);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setIsLoading(true);
        const apiConfig = {
          endPoint: buildEndPoint,
        };
        const list = await fetchSelectOptions(apiConfig);
        setTemplates(list || []);
        if (list && list.length > 0) {
          setSelectedTemplate(list[0]);
        }
      } catch (error) {
        console.error('Error fetching templates:', error);
        setTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTemplates();
  }, [buildEndPoint]);

  useImperativeHandle(comRef, () => ({
    getModel: async () => {
      const element = document.querySelector('#invalid-variables-alert');
      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      if (element !== null) {
        return {
          hasValidData: false,
          payload: {},
        };
      }
      if (selectSignatureType === 'kula') {
        if (!formRef.current) {
          return {
            hasValidData: false,
            payload: {},
          };
        }
        return formRef.current.getModel();
      } else {
        if (!esignFormRef.current) {
          return {
            hasValidData: false,
            payload: {},
          };
        }
        return esignFormRef.current.getModel();
      }
    },
  }));

  const selectOnChange = useCallback((value: any) => {
    setSelectedTemplate(value);
  }, []);

  const handleAttachmentRemove = async (file: any) => {
    await offerDetailsAPI.deleteAttachment({
      offerId: currentOffer?.id,
      attachmentId: file?.id,
    });
  };

  if (!offerId || !contactId) {
    return (
      <Flex h="100%" w="100%" justifyContent="center" alignItems="center">
        <Spinner color="primary-600" />
      </Flex>
    );
  }

  if (selectSignatureType === 'kula') {
    return (
      <Flex
        gap="12px"
        flexDir="column"
        w="100%"
        className="offer-email-container"
        data-test-id="offer-email-container"
      >
        <KText size="lg-semi-bold" color="secondary-900">
          Send offer with Kula
        </KText>
        <KText size="sm-medium" color="secondary-700" mb="8px">
          Compose your email to send the offer letter to the candidate
        </KText>

        {isLoading ? (
          <Flex h="200px" w="100%" justifyContent="center" alignItems="center">
            <Spinner color="primary-600" />
          </Flex>
        ) : (
          <Flex flexDir="column" gap="16px" w="100%">
            {/* Email Form */}
            <OfferEmailForm
              ref={formRef}
              selectedTemplate={selectedTemplate}
              attachments={attachments}
              variables={variables}
              onAttachmentRemove={handleAttachmentRemove}
              templates={templates}
              onTemplateChange={selectOnChange}
            />
          </Flex>
        )}
      </Flex>
    );
  }

  const integrationTypeMap = {
    docusign: 'docusign',
    pandadoc: 'pandadocs',
    adobe: 'adobe',
  };

  return (
    <EsignEmailForm
      ref={esignFormRef}
      integrationType={integrationTypeMap[selectSignatureType] as any}
      isAdmin={false}
    />
  );
});

OfferEmail.displayName = 'OfferEmail';
